import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Check, Download, RotateCcw } from 'lucide-react';
import dayjs from "dayjs";
import DateLogic from '../../utils/DateLogic';
import '../../CSS/Photobooth.css';

const PHOTO_COUNT = 3;
const COUNTDOWN_FROM = 3;

/* ─── Countdown Overlay ─────────────────────────────────── */
const Countdown = ({ count }) => (
  <div className="pb-countdown-overlay">
    <span key={count} className="pb-countdown-number">{count}</span>
  </div>
);

/* ─── Single Photo Slot (Landscape format) ──────────────── */
const PhotoSlot = ({ photo, index }) => (
  <div className={`pb-photo-slot ${photo ? 'has-photo' : ''}`}>
    {photo ? (
      <>
        <img src={photo} alt={`Photo ${index + 1}`} className="pb-photo-img" />
        <div className="pb-photo-badge done">
          <Check size={12} strokeWidth={3} />
          <span>Photo {index + 1}</span>
        </div>
      </>
    ) : (
      <div className="pb-photo-placeholder">
        <Camera size={22} className="pb-placeholder-icon" />
        <span className="pb-placeholder-num">Foto {index + 1} (Landscape)</span>
      </div>
    )}
  </div>
);

/* ─── Main Photobooth Component ──────────────────────────── */
const PhotoboothCapture = ({ cardData, onDone }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [photos, setPhotos] = useState([]);          // captured data URLs
  const [countdown, setCountdown] = useState(null);  // null | 1 | 2 | 3
  const [capturing, setCapturing] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [facingMode, setFacingMode] = useState('user'); // 'user' = front, 'environment' = back
  const [cameraError, setCameraError] = useState(null);
  const [flash, setFlash] = useState(false);

  /* ── Start camera ───────────────────────────────────────── */
  const startCamera = useCallback(async (mode = facingMode) => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
    setCameraReady(false);
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: mode,
          width: { ideal: 1280 },
          height: { ideal: 960 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => setCameraReady(true);
      }
    } catch (err) {
      setCameraError('Kamera tidak dapat diakses. Sila benarkan kebenaran kamera.');
    }
  }, [facingMode]);

  useEffect(() => {
    startCamera();
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  /* ── Flip camera ────────────────────────────────────────── */
  const flipCamera = () => {
    const next = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(next);
    startCamera(next);
  };

  /* ── Capture a single frame in 4:3 landscape format ──────── */
  const captureFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return null;

    const vw = video.videoWidth || 1280;
    const vh = video.videoHeight || 960;
    const targetAspect = 4 / 3; // Landscape 4:3

    let sx = 0, sy = 0, sw = vw, sh = vh;
    if (vw / vh > targetAspect) {
      sw = vh * targetAspect;
      sx = (vw - sw) / 2;
    } else {
      sh = vw / targetAspect;
      sy = (vh - sh) / 2;
    }

    canvas.width = 1200;
    canvas.height = 900; // 4:3 landscape HD
    const ctx = canvas.getContext('2d');

    // Mirror front-facing camera
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.94);
  };

  /* ── Run countdown then capture ─────────────────────────── */
  const runCountdownAndCapture = () => {
    return new Promise((resolve) => {
      let count = COUNTDOWN_FROM;
      setCountdown(count);
      const tick = setInterval(() => {
        count -= 1;
        if (count === 0) {
          clearInterval(tick);
          setCountdown(null);
          resolve(captureFrame());
        } else {
          setCountdown(count);
        }
      }, 1000);
    });
  };

  /* ── Capture sequence (3 photos) ────────────────────────── */
  const startCapture = async () => {
    if (capturing || photos.length >= PHOTO_COUNT) return;
    setCapturing(true);
    const captured = [...photos];
    for (let i = captured.length; i < PHOTO_COUNT; i++) {
      const dataUrl = await runCountdownAndCapture();
      if (dataUrl) {
        setFlash(true);
        setTimeout(() => setFlash(false), 220);
        captured.push(dataUrl);
        setPhotos([...captured]);
      }
      if (i < PHOTO_COUNT - 1) {
        // brief pause between shots
        await new Promise((r) => setTimeout(r, 800));
      }
    }
    setCapturing(false);
  };

  const retake = () => setPhotos([]);
  const allDone = photos.length === PHOTO_COUNT;

  return (
    <div className="pb-capture-layout">
      {/* Main Column: Viewfinder (Landscape format) */}
      <div className="pb-viewfinder-col">
        <div className="pb-viewfinder pb-viewfinder-landscape">
          {cameraError ? (
            <div className="pb-camera-error">
              <Camera size={40} />
              <p>{cameraError}</p>
            </div>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={`pb-video ${facingMode === 'user' ? 'pb-video-mirror' : ''}`}
            />
          )}
          {countdown !== null && <Countdown count={countdown} />}
          {flash && <div className="pb-flash" />}
        </div>

        {/* Hidden canvas for frame capture */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {/* Actions Controls */}
        <div className="pb-actions">
          {!allDone ? (
            <>
              <button
                className="pb-capture-btn"
                onClick={startCapture}
                disabled={capturing || !cameraReady || !!cameraError}
                aria-label="Take Photos"
              >
                <Camera size={19} strokeWidth={2.2} />
                <span>
                  {capturing
                    ? `Taking photo ${photos.length + 1} / ${PHOTO_COUNT}...`
                    : photos.length === 0
                      ? 'Take Photos'
                      : `Continue (${photos.length}/${PHOTO_COUNT})`}
                </span>
              </button>
              <button className="pb-flip-btn" onClick={flipCamera} aria-label="Flip camera" title="Tukar Kamera">
                <RotateCcw size={17} strokeWidth={2} />
              </button>
            </>
          ) : (
            <>
              <button className="pb-done-btn" onClick={() => onDone(photos)} aria-label="Lihat strip">
                <Download size={18} strokeWidth={2.2} />
                <span>View Photos</span>
              </button>
              <button className="pb-retake-btn" onClick={retake} aria-label="Ambil semula">
                <RotateCcw size={16} strokeWidth={2} />
                <span>Retake</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Vertical 3-Grid Photo Strip Preview (3 landscape slots stacked vertically) */}
      <div className="pb-vertical-strip-preview">
        <div className="pb-vertical-strip-card">
          <div className="pb-vertical-strip-header">
            <span className="pb-vertical-strip-title">Photo Preview</span>
          </div>

          <div className="pb-vertical-slots">
            {Array.from({ length: PHOTO_COUNT }).map((_, i) => (
              <PhotoSlot key={i} photo={photos[i]} index={i} />
            ))}
          </div>

          <div className="pb-vertical-strip-footer">
            <span className="pb-vertical-strip-couple">
              {cardData?.greetingName || 'Digital Photobooth'}
            </span>
            <span className="pb-vertical-strip-date">{DateLogic.toDay(dayjs(cardData?.eventDate).format('dddd'), cardData?.language)}, {dayjs(cardData?.eventDate).format('D MMMM YYYY')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoboothCapture;
